import { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import CreateInvoiceModal from "./CreateInvoiceModal";
import QBLinkModal from "../quickbooks/QBLinkModal";
import NewQBCustomerModal from "../quickbooks/NewQBCustomerModal";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES } from "../../config/constants";
import { auth, db } from "../../firebase";
import { qbCall } from "../../utils/api";
import { btnP, btnS } from "../../utils/styles";

export default function InvoiceTab({ client, onUpdate, theme }) {
  const t = THEMES[theme];
  const [invoices, setInvoices] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  useEffect(() => {
    if (!client.id) return;
    // Filter client-side to avoid composite index requirement
    const unsub = onSnapshot(
      collection(db, "invoices"),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setInvoices(all.filter(i => i.crmClientId === client.id)
          .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0)));
      }
    );
    return unsub;
  }, [client.id]);

  const [sendingId, setSendingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [qbCustomers, setQbCustomers] = useState([]);
  const [showQbLink, setShowQbLink] = useState(false);
  const [showQbCreate, setShowQbCreate] = useState(false);
  const [linkMsg, setLinkMsg] = useState("");
  const [linkedLocal, setLinkedLocal] = useState(null); // local override after linking

  // Load QB customers from Firestore for linking
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "qb_customers"), snap => {
      setQbCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const handleQbLink = async (crmClientId, qbCustomerId, qbCustomerName) => {
    try {
      await qbCall("qbLinkClient", { crmClientId, qbCustomerId, qbCustomerName });
      setLinkedLocal({ qbCustomerId, qbCustomerName });
      setShowQbLink(false);
      setShowQbCreate(false);
      setLinkMsg("✓ Linked to " + (qbCustomerName || "QuickBooks"));
      setTimeout(() => setLinkMsg(""), 4000);
    } catch (e) { setLinkMsg("Error: " + e.message); }
  };

  const hasQbLink = linkedLocal || (client.qbCustomerIds?.length > 0) || !!client.qbCustomerId;

  const handleSync = async () => {
    if (!hasQbLink) return;
    setSyncing(true); setSyncMsg("");
    try {
      const res = await qbCall("qbManualSyncInvoices", { crmClientId: client.id });
      const parts = [`Synced ${res.synced} invoice(s)`]; if(res.deleted>0) parts.push(`removed ${res.deleted} deleted`); setSyncMsg("✓ " + parts.join(", ") + ".");
    } catch (e) { setSyncMsg("⚠ " + (e.message||"Sync failed. Check QB connection.")); }
    setSyncing(false);
  };

  const handleSendInvoice = async (inv) => {
    if (!inv.qbInvoiceId) { setSyncMsg("⚠ No QB invoice ID found."); return; }
    setSendingId(inv.id); setSyncMsg("");
    try {
      // Send via QB's native invoice email system
      await qbCall("qbSendInvoiceQB", {
        qbInvoiceId: String(inv.qbInvoiceId),
        customerEmail: client.email || undefined,
        firestoreInvoiceId: inv.id,
      });
      setSyncMsg(`✓ Invoice #${inv.invoiceNumber||inv.qbInvoiceId} sent via QuickBooks to ${client.email || "customer's QB email"}`);
    } catch(e) { setSyncMsg("⚠ " + e.message); }
    setSendingId(null);
  };

  const handleInvoiceCreated = () => {
    setShowCreate(false);
    setSyncMsg("Invoice created successfully!");
  };

  const statusColor = (s) => s === "Paid" ? BRAND.green : s === "Unpaid" ? "#f87171" : "#f59e0b";

  return (
    <div>
      {showCreate ? (
        <CreateInvoiceModal client={client} onClose={() => setShowCreate(false)} onCreated={handleInvoiceCreated} theme={theme} />
      ) : (<>

      {/* QB link status banner */}
      {!hasQbLink ? (
        <div style={{ background: theme === "dark" ? "#1a1000" : "#fffbeb", border: "1px solid #f59e0b40", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Icon name="warning" size={16} color="#f59e0b" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>Not linked to QuickBooks</div>
              <div style={{ fontSize: 12, color: t.textMuted }}>Link to an existing QB customer or create a new one to start invoicing.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowQbLink(true)} style={{ ...btnS(t), padding: "7px 14px", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <Icon name="link" size={12} color={BRAND.blue} />Link Existing QB Customer
            </button>
            <button onClick={() => setShowQbCreate(true)} style={{ ...btnP, padding: "7px 14px", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <Icon name="plus" size={12} color="#fff" />Create New QB Customer
            </button>
          </div>
          {linkMsg && <div style={{ marginTop: 8, fontSize: 12, color: linkMsg.startsWith("✓") ? BRAND.green : "#f87171", fontWeight: 600 }}>{linkMsg}</div>}
        </div>
      ) : (
        <div style={{ background: theme === "dark" ? "#0e2818" : "#edfaf0", border: `1px solid ${BRAND.green}30`, borderRadius: 10, padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="check" size={14} color={BRAND.green} />
          <div style={{ flex: 1, fontSize: 12.5, color: t.textSub }}>Linked to QB: {linkedLocal ? <span style={{ color: BRAND.green, fontWeight: 700 }}>{linkedLocal.qbCustomerName}</span> : (client.qbCustomerIds || []).map(l => <span key={l.qbCustomerId} style={{ color: BRAND.green, fontWeight: 700 }}>{l.qbCustomerName || l.qbCustomerId}</span>).reduce((a, b) => a.length ? [...a, <span key={"sep"+a.length} style={{ color: t.textMuted }}>{", "}</span>, b] : [b], []) || <span style={{ color: BRAND.green, fontWeight: 700 }}>{client.qbCustomerName}</span>}</div>
          <button onClick={handleSync} disabled={syncing} style={{ ...btnS(t), padding: "5px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
            {syncing ? <MiniSpinner size={12} color={BRAND.blue}/> : <Icon name="refresh" size={12} color={BRAND.blue} />}
            {syncing ? <><MiniSpinner size={13} color={BRAND.blue}/>Syncing...</> : "Sync from QB"}
          </button>
        </div>
      )}

      {syncMsg && <div style={{ background: BRAND.green + "18", border: `1px solid ${BRAND.green}30`, borderRadius: 8, padding: "9px 14px", color: BRAND.green, fontSize: 12.5, marginBottom: 14 }}>{syncMsg}</div>}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: t.text }}>Invoices ({invoices.length})</div>
        <button onClick={() => setShowCreate(true)} style={{ ...btnP, opacity: !hasQbLink ? 0.5 : 1, cursor: !hasQbLink ? "not-allowed" : "pointer" }}>
          <Icon name="plus" size={14} color="#fff" />Create Invoice
        </button>
      </div>

      {/* Invoice list */}
      {invoices.length === 0 ? (
        <div style={{ background: t.bg, borderRadius: 12, padding: 36, border: `2px dashed ${t.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🧾</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: t.textSub, marginBottom: 6 }}>No invoices yet</div>
          <div style={{ fontSize: 12.5, color: t.textMuted }}>Create your first invoice to send to {client.name}.</div>
        </div>
      ) : (
        <div>
          {invoices.map(inv => (
            <div key={inv.id} style={{ background: t.bg, borderRadius: 10, padding: "14px 16px", border: `1px solid ${t.border}`, marginBottom: 10, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: statusColor(inv.status) + "18", border: `1px solid ${statusColor(inv.status)}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="invoice" size={18} color={statusColor(inv.status)} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: t.text }}>Invoice #{inv.invoiceNumber || inv.qbInvoiceId}</span>
                  <span style={{ fontSize: 11, fontWeight: 800, color: statusColor(inv.status), background: statusColor(inv.status) + "18", padding: "1px 8px", borderRadius: 10, border: `1px solid ${statusColor(inv.status)}30` }}>{inv.status}</span>
                  {inv.emailSent && <span style={{ fontSize: 10.5, color: t.textMuted, background: t.border + "40", padding: "1px 7px", borderRadius: 8 }}>📧 Sent</span>}
                </div>
                <div style={{ fontSize: 11.5, color: t.textMuted }}>
                  {inv.dueDate && `Due: ${inv.dueDate} · `}
                  {inv.memo && `${inv.memo} · `}
                  {inv.lineItems?.length || 0} line item(s)
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: t.text }}>${(inv.totalAmount || 0).toFixed(2)}</div>
                {inv.balance > 0 && <div style={{ fontSize: 11.5, color: "#f87171", fontWeight: 600 }}>Balance: ${inv.balance.toFixed(2)}</div>}
                <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                  <button onClick={()=>{const url=inv.invoiceLink||`https://app.qbo.intuit.com/app/invoice?txnId=${inv.qbInvoiceId}`;navigator.clipboard.writeText(url).then(()=>{setCopiedId(inv.id);setTimeout(()=>setCopiedId(null),2000);}).catch(()=>{window.prompt("Copy this link:",url);});}}
                    style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:7, background:copiedId===inv.id?BRAND.green+"18":t.bg, border:`1px solid ${copiedId===inv.id?BRAND.green+"40":t.border}`, color:copiedId===inv.id?BRAND.green:t.textMuted, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"'Outfit',sans-serif", whiteSpace:"nowrap", transition:"all 0.15s" }}>
                    {copiedId===inv.id ? "✓ Copied" : inv.invoiceLink ? "🔗 Payment Link" : "🔗 Copy Link"}
                  </button>
                  {inv.status !== "Paid" && (
                    <button onClick={()=>handleSendInvoice(inv)} disabled={sendingId===inv.id}
                      style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:7, background:inv.emailSent?t.bg:BRAND.blue+"18", border:`1px solid ${inv.emailSent?t.border:BRAND.blue+"40"}`, color:inv.emailSent?t.textMuted:BRAND.blue, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"'Outfit',sans-serif", whiteSpace:"nowrap" }}>
                      {sendingId===inv.id ? <><MiniSpinner size={10} color={BRAND.blue}/>Sending...</> : inv.emailSent ? "📧 Resend" : "📧 Send"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {/* Summary */}
          {invoices.length > 0 && (
            <div style={{ display: "flex", gap: 12, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.border}` }}>
              {[
                { label: "Total Billed", val: "$" + invoices.reduce((s, i) => s + (i.totalAmount || 0), 0).toFixed(2), color: t.text },
                { label: "Outstanding", val: "$" + invoices.reduce((s, i) => s + (i.balance || 0), 0).toFixed(2), color: "#f87171" },
                { label: "Paid", val: "$" + invoices.reduce((s, i) => s + ((i.totalAmount || 0) - (i.balance || 0)), 0).toFixed(2), color: BRAND.green },
              ].map(x => (
                <div key={x.label} style={{ flex: 1, background: t.surface, borderRadius: 10, padding: "12px 16px", border: `1px solid ${t.border}` }}>
                  <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>{x.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: x.color }}>{x.val}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </>)}

      {/* QB Link Modal */}
      {showQbLink && (
        <QBLinkModal
          crmClient={client}
          qbCustomers={qbCustomers}
          onLink={handleQbLink}
          onClose={() => setShowQbLink(false)}
          theme={theme}
        />
      )}

      {/* QB Create Customer Modal */}
      {showQbCreate && (
        <NewQBCustomerModal
          onClose={() => setShowQbCreate(false)}
          onCreated={async (newCust) => {
            await handleQbLink(client.id, newCust.Id || newCust.id, newCust.DisplayName || newCust.displayName);
          }}
          theme={theme}
          prefill={{ displayName: client.name, email: client.email, phone: client.phone }}
        />
      )}
    </div>
  );
}

